import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import {
  Trash2,
  Plus,
  GripVertical,
  Pen,
  FlaskConical,
  MoreVertical,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow, getFieldCategories } from "../createPluginApis";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import FieldValueTypeSelector from "../common/FieldValueTypeSelector";
import ExpressionEditor from "../common/ExpressionEditor";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { useQuery } from "react-query";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface EditFieldsNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  mode?: "manual" | "json";
  fields?: FieldMapping[];
  includeOtherInputFields?: boolean;
  globalFieldType?: "fixed" | "expression";
  inputFieldsMode?: "all" | "selected" | "except";
  inputFieldsList?: string;
  stripBinaryData?: boolean;
  supportDotNotation?: boolean;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

interface FieldMapping {
  id: string;
  fieldName: string;
  fieldValue: string;
  fieldType: "fixed" | "expression";
  fieldValueType:
    | "stringValue"
    | "numberValue"
    | "booleanValue"
    | "arrayValue"
    | "objectValue";
  category?: string;
  operation?: string;
  comparisonValue?: string;
  enabled: boolean;
}

interface OperationItem {
  display_name: string;
  value: string;
  requires_value?: boolean;
  requires_input?: boolean;
}

interface CategoryData {
  category: string;
  display_name: string;
  operations?: OperationItem[];
  sub_category?: OperationItem[];
}

const EditFieldsNode: React.FC<NodeProps<EditFieldsNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || data.header || "Edit Fields";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);
  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Mode state
  const [mode, setMode] = useState<"manual" | "json">(data.mode || "manual");

  // Fields state
  const [fields, setFields] = useState<FieldMapping[]>(data.fields || []);

  const [includeOtherInputFields, setIncludeOtherInputFields] = useState(
    data.includeOtherInputFields || false,
  );
  const [inputFieldsMode, setInputFieldsMode] = useState<
    "all" | "selected" | "except"
  >(data.inputFieldsMode || "all");
  const [inputFieldsList, setInputFieldsList] = useState<string>(
    data.inputFieldsList || "",
  );

  // Options state - track which options are added and their values
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [activeOptions, setActiveOptions] = useState<{
    supportDotNotation?: boolean;
  }>(() => {
    const options: { supportDotNotation?: boolean } = {};
    if (data.supportDotNotation !== undefined) {
      options.supportDotNotation = data.supportDotNotation;
    }
    return options;
  });

  // Computed values
  const supportDotNotation =
    activeOptions.supportDotNotation !== undefined
      ? activeOptions.supportDotNotation
      : true;

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Ref to track previous outputData for sync effect (prevent circular dependency)
  const prevSyncOutputDataRef = useRef<any>(null);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Fetch categories and operations
  const { data: categoriesData } = useQuery(
    ["fieldCategories"],
    getFieldCategories,
    {
      staleTime: Infinity, // Categories don't change often
    },
  );

  const categories: CategoryData[] = categoriesData?.data || [];

  // Helper function to get operations from a category (handles both operations and sub_category)
  const getOperationsForCategory = (categoryValue: string): OperationItem[] => {
    const category = categories.find((cat) => cat.category === categoryValue);
    if (!category) return [];
    return category.operations || category.sub_category || [];
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Auto-refresh when expanded
  useEffect(() => {
    if (!isExpanded || !reactFlowInstance) return;
    const interval = setInterval(() => {
      const edges = reactFlowInstance.getEdges();
      const nodes = reactFlowInstance.getNodes();
      setInputData(getConnectedInputs(id, edges, nodes));
    }, 1000);
    return () => clearInterval(interval);
  }, [isExpanded, reactFlowInstance, id]);

  // Sync inputData from data.inputData prop (set during execution)
  // This ensures input data updates when workflow execution completes
  useEffect(() => {
    const propInputData = (data as any).inputData;

    if (!propInputData || !Array.isArray(propInputData)) {
      return;
    }

    // Deep comparison to avoid unnecessary updates
    const newDataStr = JSON.stringify(propInputData);
    const currentDataStr = JSON.stringify(inputData);

    if (newDataStr !== currentDataStr) {
      setInputData(propInputData);
    }
  }, [JSON.stringify((data as any).inputData)]);

  // Sync outputData from node data (updated during workflow/node execution)
  // Use stringified comparison to prevent re-renders when content hasn't changed
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
    // Deliberately using raw data reference as dependency so effect runs when data object changes
    // but we do deep comparison inside to prevent unnecessary state updates
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync data changes with parent
  // Use deep comparison to prevent circular dependency with Effect 2
  useEffect(() => {
    // Deep comparison - only sync if outputData content actually changed
    const currentOutputDataStr = JSON.stringify(outputData);
    const prevOutputDataStr = JSON.stringify(prevSyncOutputDataRef.current);

    // Skip if content hasn't changed (prevents circular dependency)
    if (currentOutputDataStr === prevOutputDataStr) {
      return;
    }

    // Update ref to track this sync
    prevSyncOutputDataRef.current = outputData;

    // Build storedParameters in backend format
    const transformedFields = (fields || [])
      .filter((f) => f.enabled && f.fieldName?.trim())
      .map((f) => ({
        name: f.fieldName,
        type: f.fieldValueType,
        [f.fieldValueType]: f.fieldValue,
        ...(f.category && { category: f.category }),
        ...(f.operation && { operation: f.operation }),
        ...(f.comparisonValue && { comparisonValue: f.comparisonValue }),
      }));

    // Parse inputFieldsList to extract field names
    let parsedFieldsList = "";
    if (inputFieldsList && inputFieldsList.trim()) {
      const expressionMatches = inputFieldsList.match(
        /\{\{\$json\.([^}]+)\}\}/g,
      );
      if (expressionMatches) {
        const fieldNames = expressionMatches.map((match) =>
          match.replace(/\{\{\$json\./g, "").replace(/\}\}/g, ""),
        );
        parsedFieldsList = fieldNames.join(",");
      } else {
        parsedFieldsList = inputFieldsList;
      }
    }

    const storedParameters: any = {
      mode,
      duplicateItem: false,
      duplicateCount: 0,
      includeOtherFields: includeOtherInputFields,
      include: inputFieldsMode,
      fields: {
        values: transformedFields,
      },
      options: {
        ignoreConversionErrors: false,
        dotNotation: supportDotNotation,
        includeBinary: false, // Disabled for now - backend not ready
        continueOnFail: false,
      },
    };

    // Add selectedFields or excludeFields based on mode
    if (inputFieldsMode === "selected" && parsedFieldsList) {
      storedParameters.selectedFields = parsedFieldsList;
    } else if (inputFieldsMode === "except" && parsedFieldsList) {
      storedParameters.excludeFields = parsedFieldsList;
    }

    data.onDataChange?.(id, {
      uid,
      title,
      description,
      mode,
      fields,
      includeOtherInputFields,
      inputFieldsMode,
      inputFieldsList,
      outputData,
      supportDotNotation: activeOptions.supportDotNotation,
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    mode,
    fields,
    includeOtherInputFields,
    inputFieldsMode,
    inputFieldsList,
    outputData,
    activeOptions,
    id,
  ]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) setUid(Date.now().toString());
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};
    if (data.title) setTitle(data.title);
    if (data.description) setDescription(data.description);
    if (params.mode) setMode(params.mode);
    if (params.includeOtherFields !== undefined)
      setIncludeOtherInputFields(params.includeOtherFields);
    if (params.include) setInputFieldsMode(params.include);

    // Load fields from storedParameters
    if (params.fields?.values && Array.isArray(params.fields.values)) {
      const loadedFields = params.fields.values.map(
        (f: any, index: number) => ({
          id: `field_${Date.now()}_${index}`,
          fieldName: f.name || "",
          fieldValue:
            f.stringValue ||
            f.numberValue ||
            f.booleanValue ||
            f.arrayValue ||
            f.objectValue ||
            "",
          fieldType: "expression" as const,
          fieldValueType: f.type || "stringValue",
          enabled: true,
        }),
      );
      setFields(loadedFields);
    }

    // Load selectedFields or excludeFields as inputFieldsList
    if (params.selectedFields) {
      const fieldNames = params.selectedFields
        .split(",")
        .map((f: string) => f.trim());
      const expressions = fieldNames
        .map((f: string) => `{{$json.${f}}}`)
        .join("");
      setInputFieldsList(expressions);
    } else if (params.excludeFields) {
      const fieldNames = params.excludeFields
        .split(",")
        .map((f: string) => f.trim());
      const expressions = fieldNames
        .map((f: string) => `{{$json.${f}}}`)
        .join("");
      setInputFieldsList(expressions);
    }

    // Load options
    if (params.options?.dotNotation !== undefined) {
      setActiveOptions((prev) => ({
        ...prev,
        supportDotNotation: params.options.dotNotation,
      }));
    }
  }, []); // Only run once on mount

  // Build payload and call API
  const applyFieldMappings = async () => {
    const enabledFields = fields.filter((f) => f.enabled && f.fieldName.trim());

    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!enabledFields.length) {
      toast({
        variant: "destructive",
        title: "No Fields Configured",
        description: "Add at least one field mapping",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the edit fields node",
        duration: 3000,
      });
      return;
    }

    // Get plugin ID from session
    const pluginId = sessionStorage.getItem("editPluginId") || "";

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Execute workflow ONCE with all nodes up to and including this node
      // Build nodes array for this execution
      const workflowNodes = executableNodes.map((node: any) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current EditFieldsNode - build from local state
          let parsedFieldsList = "";
          if (inputFieldsList && inputFieldsList.trim()) {
            const expressionMatches = inputFieldsList.match(
              /\{\{\$json\.([^}]+)\}\}/g,
            );
            if (expressionMatches) {
              const fieldNames = expressionMatches.map((match) => {
                return match.replace(/\{\{\$json\./g, "").replace(/\}\}/g, "");
              });
              parsedFieldsList = fieldNames.join(",");
            } else {
              parsedFieldsList = inputFieldsList;
            }
          }

          const nodeParameters = {
            mode,
            duplicateItem: false,
            duplicateCount: 0,
            includeOtherFields: includeOtherInputFields,
            include: inputFieldsMode,
            fields: {
              values: enabledFields.map((f) => ({
                name: f.fieldName,
                type: f.fieldValueType,
                [f.fieldValueType]: f.fieldValue,
                ...(f.category && { category: f.category }),
                ...(f.operation && { operation: f.operation }),
                ...(f.comparisonValue && {
                  comparisonValue: f.comparisonValue,
                }),
              })),
            },
            options: {
              ignoreConversionErrors: false,
              dotNotation: supportDotNotation,
              includeBinary: false,
              continueOnFail: false,
            },
            ...(inputFieldsMode === "selected" &&
              parsedFieldsList && { selectedFields: parsedFieldsList }),
            ...(inputFieldsMode === "except" &&
              parsedFieldsList && { excludeFields: parsedFieldsList }),
          };

          nodeType = "data_transformation";
          subType = "set_node";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          // Determine backend type and sub_type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Get parameters using centralized utility
          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes - only in execution path
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(
        executableNodes.map((n: any) => n.id),
      );

      const connections: any = {};
      // Only include edges between nodes in the execution path
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // For if_else nodes, route to different output arrays based on handle
          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            // true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build workflow payload
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Edit Fields executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Edit Fields executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Edit Fields execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Edit Fields Execution Error:", error);
      setErrorResponse(error?.message || "Edit Fields execution failed");
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Edit Fields execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Edit Fields execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const addField = () => {
    setFields([
      ...fields,
      {
        id: Date.now().toString(),
        fieldName: "",
        fieldValue: "",
        fieldType: "expression",
        fieldValueType: "stringValue",
        category: "",
        operation: "",
        comparisonValue: "",
        enabled: true,
      },
    ]);
  };

  const removeField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
  };

  // Handle option add/remove
  const addOption = (option: "supportDotNotation") => {
    setActiveOptions((prev) => ({
      ...prev,
      [option]: true,
    }));
    setShowOptionsMenu(false);
  };

  const removeOption = (option: "supportDotNotation") => {
    setActiveOptions((prev) => {
      const newOptions = { ...prev };
      delete newOptions[option];
      return newOptions;
    });
  };

  const updateOption = (option: "supportDotNotation", value: boolean) => {
    setActiveOptions((prev) => ({
      ...prev,
      [option]: value,
    }));
  };

  const updateField = (fieldId: string, updates: Partial<FieldMapping>) => {
    setFields(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = React.useCallback((input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    // Helper function to extract json_data from backend output format
    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        // If array contains objects with json_data field, extract it
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      // If single object with json_data field, extract it
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    // Priority: response > outputData > configData
    if (input.response) {
      const extracted = extractJsonData(input.response);
      return extracted;
    }

    if (input.outputData) {
      const extracted = extractJsonData(input.outputData);
      return extracted;
    }

    // For configData, create an object
    const displayData: any = {};
    if (input.configData) {
      // Get list of encrypted fields from isDecript array
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          // Mask encrypted fields with asterisks
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  }, []);

  // Extract available fields from input data for auto-completion
  const getAvailableFields = React.useMemo((): Array<{
    path: string;
    value: any;
  }> => {
    const fields: Array<{ path: string; value: any }> = [];

    // Get the raw data from all input sources
    // Priority: use first input source for primary data
    if (inputData && inputData.length > 0) {
      const firstInput = inputData[0];
      const data = getDisplayData(firstInput);

      // Add the root data as $json so ExpressionEditor can traverse it dynamically
      fields.push({ path: "$json", value: data });

      // Also extract top-level fields for backwards compatibility
      const extractFields = (obj: any, prefix = "$json") => {
        if (obj && typeof obj === "object" && !Array.isArray(obj)) {
          Object.entries(obj).forEach(([key, value]) => {
            const path = `${prefix}.${key}`;
            fields.push({ path, value });

            // Recursively extract nested fields (limit depth to avoid too many suggestions)
            if (
              value &&
              typeof value === "object" &&
              !Array.isArray(value) &&
              prefix.split(".").length < 4
            ) {
              extractFields(value, path);
            }
          });
        } else if (Array.isArray(obj) && obj.length > 0) {
          // For arrays, extract fields from first item
          extractFields(obj[0], prefix);
        }
      };

      extractFields(data);
    }

    return fields;
  }, [JSON.stringify(inputData), getDisplayData]);

  // Handle field reordering
  const handleFieldDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("fieldIndex", index.toString());

    // Add visual feedback - reduce opacity of dragged item
    const target = e.currentTarget.closest(".field-row");
    if (target) {
      target.classList.add("opacity-50");
    }
  };

  const handleFieldDragEnd = (e: React.DragEvent) => {
    // Remove visual feedback from dragged item
    const target = e.currentTarget.closest(".field-row");
    if (target) {
      target.classList.remove("opacity-50");
    }
  };

  const handleFieldDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("fieldIndex"));

    if (dragIndex === dropIndex) return;

    const newFields = [...fields];
    const [draggedField] = newFields.splice(dragIndex, 1);
    newFields.splice(dropIndex, 0, draggedField);

    setFields(newFields);
  };

  // Handle drop on entire card - auto-populates all fields or appends to existing
  const handleDropOnCard = (e: React.DragEvent, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));

    // Find the current field to check if it has existing data
    const currentField = fields.find((f) => f.id === fieldId);

    // Auto-detect value type from dragged data
    let detectedType: FieldMapping["fieldValueType"] = "stringValue";
    if (dragData.value !== undefined && dragData.value !== null) {
      if (Array.isArray(dragData.value)) {
        detectedType = "arrayValue";
      } else if (typeof dragData.value === "object") {
        detectedType = "objectValue";
      } else if (typeof dragData.value === "number") {
        detectedType = "numberValue";
      } else if (typeof dragData.value === "boolean") {
        detectedType = "booleanValue";
      } else {
        detectedType = "stringValue";
      }
    }

    // Extract field name from path (last segment)
    // Strip {{ }} wrappers first
    let cleanPath = dragData.path
      .replace(/^\{\{\s*/, "")
      .replace(/\s*\}\}$/, "");
    // Remove $json. or config. prefix
    cleanPath = cleanPath.replace(/^\$json\./, "").replace(/^config\./, "");
    // Clean up multiple dots
    const normalizedPath = cleanPath
      .replace(/\.+/g, ".")
      .replace(/^\.|\.$/g, "");
    const pathParts = normalizedPath.split(".");
    const fieldName = pathParts[pathParts.length - 1].replace(/\[\d+\]/g, "");

    const newExpression = `{{$json.${normalizedPath}}}`;

    // If field already has a value, check for duplicates
    if (currentField?.fieldValue && currentField.fieldValue.trim()) {
      // Check if this exact expression already exists in the field value
      if (currentField.fieldValue.includes(newExpression)) {
        // Duplicate found - create new field instead
        setFields([
          ...fields,
          {
            id: Date.now().toString(),
            fieldName: fieldName,
            fieldValue: newExpression,
            fieldType: "expression",
            fieldValueType: detectedType,
            category: "",
            operation: "",
            comparisonValue: "",
            enabled: true,
          },
        ]);
      } else {
        // Not a duplicate - append to existing value
        updateField(fieldId, {
          fieldValue: `${currentField.fieldValue}${newExpression}`,
        });
      }
    } else {
      // Field is empty - populate it completely
      updateField(fieldId, {
        fieldName: fieldName,
        fieldValue: newExpression,
        fieldValueType: detectedType,
      });
    }
  };

  // Handle drop on value input - appends to existing value for multiple expressions
  const handleDropOnValue = (
    e: React.DragEvent,
    fieldId: string,
    currentValue: string,
  ) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent card's drop handler from firing
    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));

    // Strip {{ }} wrappers first
    let cleanPath = dragData.path
      .replace(/^\{\{\s*/, "")
      .replace(/\s*\}\}$/, "");
    // Remove $json. or config. prefix
    cleanPath = cleanPath.replace(/^\$json\./, "").replace(/^config\./, "");
    // Clean up multiple dots
    const normalizedPath = cleanPath
      .replace(/\.+/g, ".")
      .replace(/^\.|\.$/g, "");
    const newExpression = `{{$json.${normalizedPath}}}`;

    // Check if this expression already exists in the current value
    if (currentValue && currentValue.includes(newExpression)) {
      // Duplicate found - create new field instead
      const pathParts = normalizedPath.split(".");
      const fieldName = pathParts[pathParts.length - 1].replace(/\[\d+\]/g, "");

      // Auto-detect value type
      let detectedType: FieldMapping["fieldValueType"] = "stringValue";
      if (dragData.value !== undefined && dragData.value !== null) {
        if (Array.isArray(dragData.value)) {
          detectedType = "arrayValue";
        } else if (typeof dragData.value === "object") {
          detectedType = "objectValue";
        } else if (typeof dragData.value === "number") {
          detectedType = "numberValue";
        } else if (typeof dragData.value === "boolean") {
          detectedType = "booleanValue";
        } else {
          detectedType = "stringValue";
        }
      }

      setFields([
        ...fields,
        {
          id: Date.now().toString(),
          fieldName: fieldName,
          fieldValue: newExpression,
          fieldType: "expression",
          fieldValueType: detectedType,
          category: "",
          operation: "",
          comparisonValue: "",
          enabled: true,
        },
      ]);
    } else {
      // Not a duplicate - append to existing value
      const updatedValue = currentValue
        ? `${currentValue}${newExpression}`
        : newExpression;

      updateField(fieldId, {
        fieldValue: updatedValue,
      });
    }
  };

  // Handle drop on empty state - creates a new field with populated data
  const handleDropOnEmptyState = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));

    // Auto-detect value type from dragged data
    let detectedType: FieldMapping["fieldValueType"] = "stringValue";
    if (dragData.value !== undefined && dragData.value !== null) {
      if (Array.isArray(dragData.value)) {
        detectedType = "arrayValue";
      } else if (typeof dragData.value === "object") {
        detectedType = "objectValue";
      } else if (typeof dragData.value === "number") {
        detectedType = "numberValue";
      } else if (typeof dragData.value === "boolean") {
        detectedType = "booleanValue";
      } else {
        detectedType = "stringValue";
      }
    }

    // Extract field name from path (last segment)
    // Strip {{ }} wrappers first
    let cleanPath = dragData.path
      .replace(/^\{\{\s*/, "")
      .replace(/\s*\}\}$/, "");
    // Remove $json. or config. prefix
    cleanPath = cleanPath.replace(/^\$json\./, "").replace(/^config\./, "");
    // Clean up multiple dots
    const normalizedPath = cleanPath
      .replace(/\.+/g, ".")
      .replace(/^\.|\.$/g, "");
    const pathParts = normalizedPath.split(".");
    const fieldName = pathParts[pathParts.length - 1].replace(/\[\d+\]/g, "");

    // Create new field with populated data
    setFields([
      {
        id: Date.now().toString(),
        fieldName: fieldName,
        fieldValue: `{{$json.${normalizedPath}}}`,
        fieldType: "expression",
        fieldValueType: detectedType,
        enabled: true,
      },
    ]);
  };

  // Handle drop on fields container - adds a new field with populated data
  const handleDropOnFieldsContainer = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));

    // Auto-detect value type
    let detectedType: FieldMapping["fieldValueType"] = "stringValue";
    if (dragData.value !== undefined && dragData.value !== null) {
      if (Array.isArray(dragData.value)) {
        detectedType = "arrayValue";
      } else if (typeof dragData.value === "object") {
        detectedType = "objectValue";
      } else if (typeof dragData.value === "number") {
        detectedType = "numberValue";
      } else if (typeof dragData.value === "boolean") {
        detectedType = "booleanValue";
      } else {
        detectedType = "stringValue";
      }
    }

    // Extract field name from path
    // Strip {{ }} wrappers first
    let cleanPath = dragData.path
      .replace(/^\{\{\s*/, "")
      .replace(/\s*\}\}$/, "");
    // Remove $json. or config. prefix
    cleanPath = cleanPath.replace(/^\$json\./, "").replace(/^config\./, "");
    // Clean up multiple dots
    const normalizedPath = cleanPath
      .replace(/\.+/g, ".")
      .replace(/^\.|\.$/g, "");
    const pathParts = normalizedPath.split(".");
    const fieldName = pathParts[pathParts.length - 1].replace(/\[\d+\]/g, "");

    // Add new field with populated data to existing fields
    setFields([
      ...fields,
      {
        id: Date.now().toString(),
        fieldName: fieldName,
        fieldValue: `{{$json.${normalizedPath}}}`,
        fieldType: "expression",
        fieldValueType: detectedType,
        enabled: true,
      },
    ]);
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Pen
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce-horizontal"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
            ref={containerRef}
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Fields Editor (3/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 pb-10">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Mode Selection */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20 mt-4">
                    <label className="text-xs font-semibold">Mode</label>
                    <Select
                      value={mode}
                      onValueChange={(value: any) => setMode(value)}
                    >
                      <SelectTrigger className="w-full text-xs h-8">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="manual" className="text-xs">
                            Manual Mapping - Edit item fields one by one
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fields List */}
                  {mode === "manual" && (
                    <div className="space-y-2">
                      {/* <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Field Mappings</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addField}
                      className="h-6 text-xs px-2"
                    >
                      <Plus size={12} className="mr-1" /> Add Field
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Drop input fields here to auto-populate
                  </div> */}

                      {fields.length > 0 && (
                        <div
                          className="space-y-2 pt-1 border-2 border-dashed border-transparent rounded-md transition-all"
                          onDragOver={(e) => {
                            // Only highlight container if not dragging over a field card
                            if (e.target === e.currentTarget) {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add(
                                "border-primary",
                                "bg-primary/5",
                                "cursor-grabbing",
                              );
                              document.body.style.cursor = "grabbing";
                            }
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove(
                              "border-primary",
                              "bg-primary/5",
                              "cursor-grabbing",
                            );
                          }}
                          onDrop={(e) => {
                            // Only handle drop on container, not on child field cards
                            if (e.target === e.currentTarget) {
                              e.currentTarget.classList.remove(
                                "border-primary",
                                "bg-primary/5",
                                "cursor-grabbing",
                              );
                              document.body.style.cursor = "default";
                              handleDropOnFieldsContainer(e);
                            }
                          }}
                        >
                          {fields.map((field, index) => (
                            <div
                              key={field.id}
                              className="field-row flex items-start gap-2 transition-all rounded-md"
                              onDragOver={(e) => {
                                // Check if it's a field reorder drag
                                if (
                                  e.dataTransfer.types.includes("fieldindex")
                                ) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.dataTransfer.dropEffect = "move";
                                  e.currentTarget.classList.add(
                                    "bg-primary/10",
                                    "border-2",
                                    "border-primary",
                                    "border-dashed",
                                  );
                                }
                              }}
                              onDragLeave={(e) => {
                                if (
                                  e.dataTransfer.types.includes("fieldindex")
                                ) {
                                  e.currentTarget.classList.remove(
                                    "bg-primary/10",
                                    "border-2",
                                    "border-primary",
                                    "border-dashed",
                                  );
                                }
                              }}
                              onDrop={(e) => {
                                // Check if it's a field reorder drop
                                if (e.dataTransfer.getData("fieldIndex")) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.classList.remove(
                                    "bg-primary/10",
                                    "border-2",
                                    "border-primary",
                                    "border-dashed",
                                  );
                                  handleFieldDrop(e, index);
                                }
                              }}
                            >
                              {/* Left Side: Drag Handle + Actions */}
                              <div className="flex flex-col items-center gap-2">
                                {/* Drag Handle */}
                                <div
                                  draggable
                                  onDragStart={(e) =>
                                    handleFieldDragStart(e, index)
                                  }
                                  onDragEnd={handleFieldDragEnd}
                                  className="cursor-grab active:cursor-grabbing pt-2 rounded hover:bg-muted/50 transition-all nodrag"
                                >
                                  <GripVertical
                                    size={20}
                                    className="text-muted-foreground hover:text-primary transition"
                                  />
                                </div>

                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={field.enabled}
                                  onChange={(e) =>
                                    updateField(field.id, {
                                      enabled: e.target.checked,
                                    })
                                  }
                                  className="w-4 h-4"
                                />

                                {/* Delete Icon */}
                                <Trash2
                                  size={16}
                                  className="cursor-pointer text-red-500 hover:text-red-700 transition"
                                  onClick={() => removeField(field.id)}
                                />
                              </div>

                              {/* Field Card */}
                              <div
                                className="flex-1 border rounded-md p-2 space-y-2 bg-background transition-all"
                                onDragOver={(e) => {
                                  // Only handle data field drops on the card
                                  if (
                                    !e.dataTransfer.types.includes("fieldindex")
                                  ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.dataTransfer.dropEffect = "copy";
                                    e.currentTarget.classList.add(
                                      "border-primary",
                                      "bg-primary/5",
                                      "cursor-grabbing",
                                    );
                                    document.body.style.cursor = "grabbing";
                                  }
                                }}
                                onDragLeave={(e) => {
                                  e.stopPropagation();
                                  e.currentTarget.classList.remove(
                                    "border-primary",
                                    "bg-primary/5",
                                    "cursor-grabbing",
                                  );
                                }}
                                onDrop={(e) => {
                                  // Only handle data field drops on the card
                                  if (!e.dataTransfer.getData("fieldIndex")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.remove(
                                      "border-primary",
                                      "bg-primary/5",
                                      "cursor-grabbing",
                                    );
                                    document.body.style.cursor = "default";
                                    handleDropOnCard(e, field.id);
                                  }
                                }}
                              >
                                {/* Field Name */}
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <label className="text-xs text-muted-foreground">
                                      Name
                                    </label>
                                  </div>
                                  <Input
                                    value={field.fieldName}
                                    onChange={(e) =>
                                      updateField(field.id, {
                                        fieldName: e.target.value,
                                      })
                                    }
                                    placeholder="Field name"
                                    className="text-xs h-7"
                                  />
                                </div>

                                {/* Field Value Type */}
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground">
                                    Value Type
                                  </label>
                                  <FieldValueTypeSelector
                                    value={field.fieldValueType}
                                    onValueChange={(value: string) =>
                                      updateField(field.id, {
                                        fieldValueType: value as any,
                                      })
                                    }
                                  />
                                </div>

                                {/* Field Value */}
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground ">
                                    Value
                                  </label>
                                  <div
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      e.currentTarget.classList.add(
                                        "cursor-grabbing",
                                        "bg-primary/10",
                                        "border-primary",
                                      );
                                      document.body.style.cursor = "grabbing";
                                    }}
                                    onDragLeave={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      e.currentTarget.classList.remove(
                                        "cursor-grabbing",
                                        "bg-primary/10",
                                        "border-primary",
                                      );
                                    }}
                                    onDrop={(e) => {
                                      e.currentTarget.classList.remove(
                                        "cursor-grabbing",
                                        "bg-primary/10",
                                        "border-primary",
                                      );
                                      document.body.style.cursor = "default";
                                      handleDropOnValue(
                                        e,
                                        field.id,
                                        field.fieldValue,
                                      );
                                    }}
                                    className="relative border border-transparent rounded transition-all"
                                  >
                                    {field.fieldType === "expression" ? (
                                      <ExpressionEditor
                                        value={field.fieldValue}
                                        onChange={(value) =>
                                          updateField(field.id, {
                                            fieldValue: value,
                                          })
                                        }
                                        placeholder="Type {{$ to see options: $json or $()"
                                        availableFields={getAvailableFields}
                                        nodes={nodes}
                                        currentNodeId={id}
                                        minHeight="32px"
                                        maxHeight="150px"
                                      />
                                    ) : (
                                      <Input
                                        value={field.fieldValue}
                                        onChange={(e) =>
                                          updateField(field.id, {
                                            fieldValue: e.target.value,
                                          })
                                        }
                                        placeholder="Enter value"
                                        className="text-xs h-7"
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Category */}
                                <div className="space-y-1">
                                  <label className="text-xs text-muted-foreground">
                                    Category (optional)
                                  </label>
                                  <Select
                                    value={field.category}
                                    onValueChange={(value: string) => {
                                      if (value === "none") value = "";
                                      updateField(field.id, {
                                        category: value,
                                        operation: "",
                                        comparisonValue: "",
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="w-full text-xs h-7">
                                      <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectGroup>
                                        <SelectItem value="none">
                                          {" "}
                                          Select Category
                                        </SelectItem>
                                        {categories.map((cat) => (
                                          <SelectItem
                                            key={cat.category}
                                            value={cat.category}
                                            className="text-xs"
                                          >
                                            {cat.display_name}
                                          </SelectItem>
                                        ))}
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Operation (dependent on category) */}
                                {field.category && (
                                  <div className="space-y-1">
                                    <label className="text-xs text-muted-foreground">
                                      Operation
                                    </label>
                                    <Select
                                      value={field.operation || ""}
                                      onValueChange={(value: string) =>
                                        updateField(field.id, {
                                          operation: value,
                                          comparisonValue: "", // Reset comparison value when operation changes
                                        })
                                      }
                                    >
                                      <SelectTrigger className="w-full text-xs h-7">
                                        <SelectValue placeholder="Select operation" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectGroup>
                                          {getOperationsForCategory(
                                            field.category,
                                          ).map((op) => (
                                            <SelectItem
                                              key={op.value}
                                              value={op.value}
                                              className="text-xs"
                                            >
                                              {op.display_name}
                                            </SelectItem>
                                          ))}
                                        </SelectGroup>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}

                                {/* Comparison Value (dependent on operation requiring value) */}
                                {field.operation &&
                                  (() => {
                                    const operations = getOperationsForCategory(
                                      field.category || "",
                                    );
                                    const selectedOp = operations.find(
                                      (op) => op.value === field.operation,
                                    );
                                    return selectedOp?.requires_value ? (
                                      <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                          Comparison Value{" "}
                                          <span className="text-destructive">
                                            *
                                          </span>
                                        </label>
                                        <Input
                                          type="text"
                                          value={field.comparisonValue || ""}
                                          onChange={(e) =>
                                            updateField(field.id, {
                                              comparisonValue: e.target.value,
                                            })
                                          }
                                          placeholder="Enter comparison value"
                                          className="text-xs h-7"
                                        />
                                      </div>
                                    ) : null;
                                  })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Always visible drop zone */}
                      <div
                        className={`border-2 border-dashed rounded-md text-center space-y-2 transition-all duration-300 ease-in-out ${
                          fields.length === 0 ? "p-8" : "p-4"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add(
                            "border-primary",
                            "bg-primary/10",
                            "cursor-grabbing",
                          );
                          document.body.style.cursor = "grabbing";
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove(
                            "border-primary",
                            "bg-primary/10",
                            "cursor-grabbing",
                          );
                        }}
                        onDrop={(e) => {
                          e.currentTarget.classList.remove(
                            "border-primary",
                            "bg-primary/10",
                            "cursor-grabbing",
                          );
                          document.body.style.cursor = "default";
                          if (fields.length === 0) {
                            handleDropOnEmptyState(e);
                          } else {
                            handleDropOnFieldsContainer(e);
                          }
                        }}
                      >
                        <div
                          className={`text-muted-foreground transition-all duration-300 ${
                            fields.length === 0 ? "text-sm" : "text-xs"
                          }`}
                        >
                          Drag Input Fields Here
                        </div>
                        {fields.length === 0 && (
                          <div className="text-xs text-muted-foreground">
                            or
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={addField}
                          className={`text-xs transition-all duration-300 ${
                            fields.length === 0 ? "" : "h-6 px-2"
                          }`}
                        >
                          <Plus size={12} className="mr-1" /> Add Field
                        </Button>
                      </div>

                      {/* Options Section */}
                      <h4 className="text-xs font-semibold mt-3 mb-2">
                        Options
                      </h4>

                      {/* Include Other Input Fields Toggle with Fixed/Expression Tabs */}
                      <div className="border-t pt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          {/* Left Side - Toggle with Tooltip */}
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={includeOtherInputFields}
                                      onCheckedChange={
                                        setIncludeOtherInputFields
                                      }
                                      className="scale-75"
                                    />
                                    <span className="text-xs font-medium">
                                      Include Other Input Fields
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">
                                    Include Other Input Fields
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>

                          {/* Right Side - Options Menu */}
                          <div className="relative flex items-center gap-1">
                            <button
                              onClick={() =>
                                setShowOptionsMenu(!showOptionsMenu)
                              }
                              className="text-xs px-1 py-0.5 rounded hover:bg-muted transition"
                            >
                              <MoreVertical size={16} />
                            </button>

                            {/* Options Dropdown */}
                            {showOptionsMenu && (
                              <>
                                {/* Backdrop to close menu */}
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setShowOptionsMenu(false)}
                                />
                                <div className="absolute right-0 top-8 z-20 bg-background border shadow-lg rounded-md py-1 min-w-[180px]">
                                  {/* Include Binary Data Option - Disabled for now (backend not ready) */}
                                  {/* {activeOptions.includeBinaryData === undefined && (
                                <div
                                  className="px-3 py-2 text-xs cursor-pointer hover:bg-accent transition"
                                  onClick={() => addOption("includeBinaryData")}
                                >
                                  Include Binary Data
                                </div>
                              )} */}

                                  {/* Support Dot Notation Option */}
                                  {activeOptions.supportDotNotation ===
                                    undefined && (
                                    <div
                                      className="px-3 py-2 text-xs cursor-pointer hover:bg-accent transition"
                                      onClick={() =>
                                        addOption("supportDotNotation")
                                      }
                                    >
                                      Support Dot Notation
                                    </div>
                                  )}

                                  {/* Show message if all options are added */}
                                  {activeOptions.supportDotNotation !==
                                    undefined && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                      All options added
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Active Options - Show added options with toggles */}
                        {activeOptions.supportDotNotation !== undefined && (
                          <div className="space-y-2 mt-3">
                            {/* Support Dot Notation Toggle */}
                            <div className="flex items-center justify-between p-2 bg-muted/20  rounded">
                              <span className="text-xs font-medium">
                                Support Dot Notation
                              </span>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={activeOptions.supportDotNotation}
                                  onCheckedChange={(checked) =>
                                    updateOption("supportDotNotation", checked)
                                  }
                                  className="scale-75"
                                />
                                <Trash2
                                  size={14}
                                  className="cursor-pointer text-red-500 hover:text-red-700 transition"
                                  onClick={() =>
                                    removeOption("supportDotNotation")
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Conditional Fields When Toggle is ON */}
                        {includeOtherInputFields && (
                          <div className="space-y-3 mt-3 pl-6 border-l-2 border-muted">
                            {/* Input Fields Mode Dropdown */}
                            <div className="space-y-1">
                              <label className="text-xs font-medium text-muted-foreground">
                                Input Fields to Include
                              </label>
                              <Select
                                value={inputFieldsMode}
                                onValueChange={(value: any) => {
                                  setInputFieldsMode(value);
                                  // Clear the list when changing modes
                                  setInputFieldsList("");
                                }}
                              >
                                <SelectTrigger className="w-full text-xs h-7">
                                  <SelectValue placeholder="Select mode" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectItem value="all" className="text-xs">
                                      All
                                    </SelectItem>
                                    <SelectItem
                                      value="selected"
                                      className="text-xs"
                                    >
                                      Selected
                                    </SelectItem>
                                    <SelectItem
                                      value="except"
                                      className="text-xs"
                                    >
                                      All Except
                                    </SelectItem>
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Conditional Input Field */}
                            {inputFieldsMode !== "all" && (
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                  {inputFieldsMode === "selected"
                                    ? "Fields to Include"
                                    : "Fields to Exclude"}
                                </label>
                                <ExpressionEditor
                                  value={inputFieldsList}
                                  onChange={setInputFieldsList}
                                  placeholder="Drag from input or type {{$json.fieldName}}"
                                  availableFields={getAvailableFields}
                                  nodes={nodes}
                                  currentNodeId={id}
                                  minHeight="28px"
                                  maxHeight="80px"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Apply Button */}
                <div className="pt-2 border-t mt-2">
                  <Button
                    onClick={applyFieldMappings}
                    disabled={
                      !inputData ||
                      inputData.length === 0 ||
                      fields.filter((f) => f.enabled && f.fieldName.trim())
                        .length === 0 ||
                      !isTitleValid
                    }
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Output Preview (4/12 columns) */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Apply field mappings to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default EditFieldsNode;
